import {
  InteractionStatus,
  EventType,
  EventMessageUtils,
  type AccountInfo,
  type EventMessage,
  type AuthenticationResult,
} from '@azure/msal-browser'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { msalInstance } from '../authConfig'
import { CustomNavigationClient } from '../router/CustomNavigationClient'
import { useRouter } from 'vue-router'

type AccountIdentifiers = Partial<Pick<AccountInfo, 'homeAccountId' | 'localAccountId' | 'username'>>

function accountArraysAreEqual(arrayA: Array<AccountIdentifiers>, arrayB: Array<AccountIdentifiers>): boolean {
  if (arrayA.length !== arrayB.length) {
    return false
  }

  const comparisonArray = [...arrayB]

  return arrayA.every((elementA) => {
    const elementB = comparisonArray.shift()
    if (!elementA || !elementB) {
      return false
    }

    return (
      elementA.homeAccountId === elementB.homeAccountId &&
      elementA.localAccountId === elementB.localAccountId &&
      elementA.username === elementB.username
    )
  })
}

export const useMsalStore = defineStore('msal', () => {
  const router = useRouter()

  // Permet à MSAL de profiter des fonctions de navigation du
  // routeur lorsque MSAL redirige entre les pages de l'application
  const navigationClient = new CustomNavigationClient(router)
  msalInstance.setNavigationClient(navigationClient)

  // Fixe l'utilisateur courant
  const allAccounts = msalInstance.getAllAccounts()
  if (allAccounts.length > 0) {
    msalInstance.setActiveAccount(allAccounts[0]!)
  }
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult
      const account = payload.account
      msalInstance.setActiveAccount(account)
    }
  })

  const inProgress = ref<InteractionStatus>(InteractionStatus.Startup)
  const accounts = ref(allAccounts)
  const activeAccount = computed(() => accounts.value.at(0) ?? null)

  msalInstance.addEventCallback((message: EventMessage) => {
    switch (message.eventType) {
      case EventType.ACCOUNT_ADDED:
      case EventType.ACCOUNT_REMOVED:
      case EventType.LOGIN_SUCCESS:
      case EventType.SSO_SILENT_SUCCESS:
      case EventType.HANDLE_REDIRECT_END:
      case EventType.LOGIN_FAILURE:
      case EventType.SSO_SILENT_FAILURE:
      case EventType.LOGOUT_END:
      case EventType.ACQUIRE_TOKEN_SUCCESS:
      case EventType.ACQUIRE_TOKEN_FAILURE: {
        const currentAccounts = msalInstance.getAllAccounts()
        if (!accountArraysAreEqual(currentAccounts, accounts.value)) {
          accounts.value = currentAccounts
        }
        break
      }
    }

    const status = EventMessageUtils.getInteractionStatusFromEvent(message, inProgress.value)
    if (status !== null) {
      inProgress.value = status
    }
  })

  return {
    inProgress,
    accounts,
    activeAccount,
    msalInstance,
  }
})
